import React, { useState, useEffect, useRef, useImperativeHandle } from 'react'
import { useLocation } from 'react-router-dom'
import { useImmer } from "use-immer"
import { Form, Input, Button, Col, Row, Modal, Space, Popover, Carousel, Checkbox, Tooltip } from 'antd'
import NewEmailVerification from '../Application/NewEmailVerification'
import "./signup.css"
import { jsPDF } from 'jspdf'
import html2canvas from 'html2canvas'
import { LoadingOutlined, QuestionCircleOutlined } from '@ant-design/icons'
import { useSelector, useDispatch } from 'react-redux'
import Terms from './terms'
import jwt_decode from 'jwt-decode'
import { useStatsigClient } from "@statsig/react-bindings/src";
import { updateStatsigUser } from "../statsig/statsigMethod"
import { track, flushThenLeave } from 'statsig'
import { STATSIG, PAGE } from 'statsig/events'
import { syncLoTokenToStrataCookies } from "../syncStrataSession.js"


const validateTeamID = (_, value) => {
    if (!value) return Promise.reject(new Error('Team ID is required!'))
    if (/^[0-9]*$/.test(value)) {
        return Promise.reject(new Error('Team ID cannot be purely numeric. Please include at least one letter.'))
    }
    return Promise.resolve()
}

const ORGANIZATION_NAME_TAKEN_MESSAGE = 'This organization name already exists. Use a different name.'

async function checkSignupOrganizationNameAvailable(organizationName) {
    const response = await fetch('/los/signup/checkorganizationname', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json;charset=utf-8' },
        body: JSON.stringify({ organizationName: organizationName.trim() }),
    })
    const data = await response.json().catch(() => ({}))
    if (!response.ok || data.Status !== 'OK') {
        throw new Error(data.Text || 'Failed to check organization name')
    }
    return !!data.Available
}

// HubSpot 配置（参考 zeitro.com-gmcc-new.html）
const HUBSPOT_FORM_URL = 'https://api.hsforms.com/submissions/v3/integration/submit/8947143/ea936cc8-74c5-43ca-9c35-9bedae7e1792'
const HUBSPOT_OWNER_ASSIGN_LAMBDA_URL = 'https://nld4wo6x6hkzmtn46w3tlxwqta0yjwgj.lambda-url.us-west-1.on.aws/'

function getHubspotUtk() {
    if (typeof document === 'undefined') return ''
    const match = document.cookie.match(/(?:^|; )hubspotutk=([^;]+)/)
    return match ? decodeURIComponent(match[1]) : ''
}

function getInitialReferrerZeitroCookie() {
    if (typeof document === 'undefined' || !document.cookie) return ''
    const match = document.cookie.match(/(?:^|; )initial_referrer_zeitro=([^;]+)/)
    return match ? decodeURIComponent(match[1]) : ''
}

function getEntryReferrer() {
    if (typeof window === 'undefined') return ''
    let ref = window.sessionStorage.getItem('los_entry_referrer') || ''
    if (!ref) ref = getInitialReferrerZeitroCookie()
    if (!ref && typeof document !== 'undefined') ref = document.referrer || ''
    return ref
}

/** 仅在上报 HubSpot 前：若 URL 无 utm_source，则根据「进入 LOS 时记录的 referrer」推断（仅影响本次上报，无副作用） */
function utmSourceMediumFromReferrer() {
    const ref = getEntryReferrer()
    if (!ref) return null
    try {
        const host = new URL(ref).hostname.toLowerCase()
        const isGoogle = host === 'google.com' || host === 'www.google.com' || host.endsWith('.google.com') || host.includes('.google.com.')
        const isBing = host === 'bing.com' || host === 'www.bing.com' || host.endsWith('.bing.com') ||host.includes('.bing.com.')
        const isYahoo = host === 'yahoo.com' || host === 'www.yahoo.com' || host.endsWith('.yahoo.com') || host.includes('.yahoo.com.') || host.includes('search.yahoo.com')
        const isDuckDuckGo = host === 'duckduckgo.com' || host === 'www.duckduckgo.com' || host.endsWith('.duckduckgo.com') || host.includes('.duckduckgo.com.')
        if (isGoogle) return { utm_source: 'google', utm_medium: 'SEO' }
        if (isBing) return { utm_source: 'bing', utm_medium: 'SEO' }
        if (isYahoo) return { utm_source: 'yahoo', utm_medium: 'SEO' }
        if (isDuckDuckGo) return { utm_source: 'duckduckgo', utm_medium: 'SEO' }
    } catch (e) { /* ignore */ }
    return null
}

async function sendSignupToHubSpot({ email, firstName, lastName, phone, company, leadSource, utmParams }) {
    // 仅在上报前补一层：URL 无 utm_source 时用 referrer 推断，只影响本次 HubSpot 上报
    let utm_source = utmParams?.utm_source || ''
    let utm_medium = utmParams?.utm_medium || ''
    if (!utm_source) {
        const ref = getEntryReferrer()
        const fromRef = utmSourceMediumFromReferrer()
        if (fromRef) {
            utm_source = fromRef.utm_source
            utm_medium = fromRef.utm_medium
        } else if (!ref) {
            utm_medium = 'Direct'
        }
    }
    const hubspotFields = [
        { name: 'firstname', value: firstName || '' },
        { name: 'lastname', value: lastName || '' },
        { name: 'email', value: email || '' },
        // { name: 'phone', value: phone || '' },
        { name: 'company', value: company || '' },
        // { name: 'why_contact_us', value: 'New LO Signup' },
        { name: 'lead_source', value: leadSource || 'Zeitro Sign Up' },
        { name: 'created_from_page', value: window.location.href || 'Zeitro LO Signup' },
        { name: 'utm_source', value: utm_source },
        { name: 'utm_medium', value: utm_medium },
        { name: 'utm_campaign', value: utmParams?.utm_campaign || '' },
        { name: 'utm_term', value: utmParams?.utm_term || '' },
        { name: 'utm_content', value: utmParams?.utm_content || '' },
    ]
    const hubspotSubmitData = {
        fields: hubspotFields,
        context: {
            hutk: getHubspotUtk(),
            pageUri: window.location.href || 'Zeitro LO Signup',
            pageName: document.title || 'Zeitro Loan Officer Sign Up',
        },
    }
    try {
        const formRes = await fetch(HUBSPOT_FORM_URL, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(hubspotSubmitData),
        })
        if (!formRes.ok) throw new Error(formRes.statusText)
        const isProduction = process.env.NODE_ENV === 'production' && process.env.REACT_APP_ENV !== 'staging' && process.env.REACT_APP_ENV !== 'development'
        const lambdaBody = { email }
        if (!isProduction) lambdaBody.ownerId = '88819382'
        const lambdaRes = await fetch(HUBSPOT_OWNER_ASSIGN_LAMBDA_URL, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(lambdaBody),
        })
        if (!lambdaRes.ok) throw new Error(lambdaRes.statusText)
    } catch (err) {
        console.warn('HubSpot signup sync failed:', err)
    }
}

function PersonalInformation(props) {
    const { signData, back, pageTime, utmSource, utmParams } = props
    const [errorInput, setError] = useState(false)
    const [goodtogo, setGoodtogo] = useState(false)
    const [flag, setFlag] = useImmer(true)
    const [showAgreement, setShowAgreement] = useState(false)
    const [form] = Form.useForm()
    const formvalues = Form.useWatch([], form)
    const [loading, setLoading] = useState(false)
    const [checked, setChecked] = useState(false)
    const [nameChecking, setNameChecking] = useState(false)
    const [nameDuplicate, setNameDuplicate] = useState(false)
    const nameCheckSeqRef = useRef(0)
    const nameCheckTimerRef = useRef(null)
    const companyNameValue = Form.useWatch('CompanyName', form)
    const dispatch = useDispatch()
    const { client } = useStatsigClient();

    const [pageTimes, setPageTimes] = useState({
        ...pageTime,
        creatCompanyPage: {
            componentTime: {
                startTime: null,
                totalDuration: 0.0
            },
            inputField: [],
            agreementPopupTime: {
                startTime: null,
                totalDuration: 0.0
            }
        },
    });

    const isFirstRender = useRef(true);
    const previousPageTimes = useRef(null);
    const [currentFocusTime, setCurrentFocusTime] = useState(null);

    useEffect(() => {
        setPageTimes(prev => ({
            ...prev,
            creatCompanyPage: {
                ...prev.creatCompanyPage,
                componentTime: {
                    ...prev.creatCompanyPage.componentTime,
                    startTime: new Date()
                }
            }
        }));
        previousPageTimes.current = new Date();
    }, [])

    useEffect(() => {
        //Update the dwell time of the input email address component
        if (showAgreement === true) {
            const currentTime = new Date();
            const duration = parseFloat(((currentTime - previousPageTimes.current) / 1000.0).toFixed(1));
            setPageTimes(prev => ({
                ...prev,
                creatCompanyPage: {
                    ...prev.creatCompanyPage,
                    componentTime: {
                        ...prev.creatCompanyPage.componentTime,
                        totalDuration: parseFloat((prev.creatCompanyPage.componentTime.totalDuration + duration).toFixed(1))
                    }
                }
            }));
        }
        if (showAgreement === false) {
            const currentTime = new Date();
            if (pageTimes.creatCompanyPage.inputField.length !== 0) {
                const duration = parseFloat(((currentTime - previousPageTimes.current) / 1000.0).toFixed(1));
                setPageTimes(prev => ({
                    ...prev,
                    creatCompanyPage: {
                        ...prev.creatCompanyPage,
                        agreementPopupTime: {
                            ...prev.creatCompanyPage.agreementPopupTime,
                            totalDuration: parseFloat(((prev.creatCompanyPage.agreementPopupTime.totalDuration || 0) + duration).toFixed(1))
                        }
                    }
                }))
            } else {
                setPageTimes(prev => ({
                    ...prev,
                    creatCompanyPage: {
                        ...prev.creatCompanyPage,
                        agreementPopupTime: {
                            ...prev.creatCompanyPage.agreementPopupTime,
                            startTime: currentTime,
                        }
                    }
                }))
            }


        }
        previousPageTimes.current = new Date();
    }, [showAgreement]);

    const handleFocus = (e) => {
        const fieldName = e.target.id;
        const fieldValue = e.target.value;

        setCurrentFocusTime(new Date());

        setPageTimes(prev => {
            const existingField = prev.creatCompanyPage.inputField.find(
                field => field.name === fieldName
            );

            if (!existingField) {
                return {
                    ...prev,
                    creatCompanyPage: {
                        ...prev.creatCompanyPage,
                        inputField: [
                            ...prev.creatCompanyPage.inputField,
                            {
                                name: fieldName,
                                value: fieldValue,
                                focusTime: 0,
                                startTime: new Date()
                            }
                        ]
                    }
                };
            }
            return prev;
        });
    };

    const handleBlur = (e) => {
        const fieldName = e.target.id;
        const fieldValue = e.target.value;

        if (currentFocusTime) {
            const endTime = new Date();
            const timeSpent = parseFloat(((endTime - currentFocusTime) / 1000.0).toFixed(1));

            setPageTimes(prev => ({
                ...prev,
                creatCompanyPage: {
                    ...prev.creatCompanyPage,
                    inputField: prev.creatCompanyPage.inputField.map(field => {
                        if (field.name === fieldName) {
                            const currentFocusTime = typeof field.focusTime === 'string'
                                ? parseFloat(field.focusTime)
                                : (field.focusTime || 0);

                            return {
                                ...field,
                                value: fieldValue,
                                focusTime: parseFloat((currentFocusTime + timeSpent).toFixed(1))
                            };
                        }
                        return field;
                    })
                }
            }));
        }

        setCurrentFocusTime(null);
    };

    useEffect(() => {
        form
            .validateFields({
                validateOnly: true,
            })
            .then(() => {
                setGoodtogo(true)
            })
            .catch(() => {
                setGoodtogo(false)
            })
    }, [form, formvalues, nameDuplicate])

    const checkOrganizationName = async () => {
        const query = (form.getFieldValue('CompanyName') || '').trim()
        const requestSeq = nameCheckSeqRef.current + 1
        nameCheckSeqRef.current = requestSeq

        if (!query) {
            setNameDuplicate(false)
            setNameChecking(false)
            return false
        }

        setNameDuplicate(false)
        setNameChecking(true)
        try {
            const available = await checkSignupOrganizationNameAvailable(query)
            if (requestSeq !== nameCheckSeqRef.current) return false
            if (!available) {
                setNameDuplicate(true)
                form.setFields([{
                    name: 'CompanyName',
                    errors: [ORGANIZATION_NAME_TAKEN_MESSAGE],
                }])
                setGoodtogo(false)
                return false
            }
            form.setFields([{ name: 'CompanyName', errors: [] }])
            return true
        } catch (err) {
            if (requestSeq !== nameCheckSeqRef.current) return false
            form.setFields([{
                name: 'CompanyName',
                errors: [err instanceof Error ? err.message : 'Failed to check organization name'],
            }])
            return false
        } finally {
            if (requestSeq === nameCheckSeqRef.current) {
                setNameChecking(false)
            }
        }
    }

    useEffect(() => {
        const query = (companyNameValue || '').trim()
        if (nameCheckTimerRef.current) {
            window.clearTimeout(nameCheckTimerRef.current)
            nameCheckTimerRef.current = null
        }
        nameCheckSeqRef.current += 1
        setNameChecking(false)
        setNameDuplicate(false)
        form.setFields([{ name: 'CompanyName', errors: [] }])

        if (!query) {
            return undefined
        }

        nameCheckTimerRef.current = window.setTimeout(() => {
            nameCheckTimerRef.current = null
            void checkOrganizationName()
        }, 1000)

        return () => {
            if (nameCheckTimerRef.current) {
                window.clearTimeout(nameCheckTimerRef.current)
                nameCheckTimerRef.current = null
            }
        }
    }, [companyNameValue])

    const handleCompanyNameBlur = (e) => {
        handleBlur(e)
        if ((form.getFieldValue('CompanyName') || '').trim() && nameCheckTimerRef.current) {
            setNameChecking(true)
        }
    }

    useEffect(() => {
        dispatch({ type: "OPEN", data: false })
        return () => {
            if (nameCheckTimerRef.current) {
                window.clearTimeout(nameCheckTimerRef.current)
            }
        }
    }, [])
    const onAcceptAgreement = async () => {
        setLoading(true)
        const currentDate = new Date();
        // console.log(signData)
        // console.log(formvalues)
        try {
            const response = await fetch('/los/createnewcustomer', {
                method: 'post',
                headers: {
                    'Content-Type': 'application/json;charset=utf-8'
                },
                body: JSON.stringify(
                    {
                        Email: signData.emailaddress.toLowerCase().trim(),
                        UTMSource: utmParams.utm_source,
                        UTMMedium: utmParams.utm_medium,
                        UTMCampaign: utmParams.utm_campaign,
                        UTMTerm: utmParams.utm_term,
                        UTMContent: utmParams.utm_content,
                        ...formvalues
                    }
                )
            })
            const { Token, Text } = await response.json()

            if (Token !== '') {
                setError(false)
                sessionStorage.setItem("ZeitroA", Token)
                syncLoTokenToStrataCookies(Token)
                // 同步到 HubSpot 并分配 owner，完成后才跳页
                await sendSignupToHubSpot({
                    email: signData.emailaddress.toLowerCase().trim(),
                    firstName: formvalues?.FirstName || '',
                    lastName: formvalues?.LastName || '',
                    phone: formvalues?.Phone || '',
                    company: formvalues?.CompanyName || '',
                    leadSource: 'Zeitro Sign Up',
                    utmParams: utmParams,
                })
                const payload = jwt_decode(Token)
                const customerId = payload.customerid
                try {
                    await updateStatsigUser(client, signData.emailaddress.toLowerCase().trim(), customerId)
                } catch (e) {
                    console.warn('statsig updateUser before signup track failed', e)
                }
                track(STATSIG.AUTH.SIGNUP, customerId, { customer_id: customerId })
                let path =
                    utmSource === 'guidelinegpt'
                        ? '/app/management?select=Pricing'
                        : '/app/mortgagegpt'

                // 根据 UTM 来源决定跳转路径

                // 普通用户流程：
                // 表单提交 → 显示服务协议 → 用户接受协议 → 创建账户 → 保存登录状态 → 跳转到付费页面 → 选择套餐和付费 → 跳转到 Strata AI 页面

                // GuidelineGPT 用户流程：
                // 表单提交 → 显示服务协议 → 用户接受协议 → 创建账户 → 保存登录状态 → 直接跳转到管理页面的定价部分

                if(utmSource !== 'guidelinegpt'){
                    // 从路由参数判断
                    const plan = new URLSearchParams(window.location.search).get('selectedPlan')
                    // free plan
                    if(plan === 'free'){
                        path = '/app/mortgagegpt'
                    }
                    // paid plan
                    if(['individual', 'business'].includes(plan)){
                        path = '/signupAndPayment?page=pay&selectedPlan=' + plan
                    }
                    //  no plan
                    if(plan === 'no'){
                        path = '/signupAndPayment?page=plan'
                    }
                }
                
                await flushThenLeave(() => {
                    if(props.onSuccess){
                        setShowAgreement(false);
                        props.onSuccess();
                    }else{
                        // 从浏览器历史记录中移除当前页面，防止用户返回重复创建账户
                        window.history.replaceState(null, '', path)
                        window.location.href = path
                    }
                })

            } else {
                if (Text.includes('customers_id_key')) {
                    setError(true)
                    setLoading(false)
                    return
                }
                if (typeof Text === 'string' && Text.toLowerCase().includes('organization name already exists')) {
                    setShowAgreement(false)
                    setNameDuplicate(true)
                    form.setFields([{
                        name: 'CompanyName',
                        errors: [ORGANIZATION_NAME_TAKEN_MESSAGE],
                    }])
                    setLoading(false)
                    return
                }
                alert("Something went wrong. Please try again later.")
                back()
            }

        } catch (error) {
            console.log(error)
            alert("Something went wrong. Please try again later.")
            back()
        }
        setLoading(false)
    }
    const onChangeCheckBox = (e) => {
        setChecked(e.target.checked)
    }
    const footer = () => {
        return (
            <div className='service-agreement-footer'>
                <div><Checkbox className='service-agreement-checkbox' checked={checked} onChange={onChangeCheckBox}>I have read and understood the Software as a Service Agreement.</Checkbox></div>
                <div className='service-agreement-btn-group'>
                    <Button className='service-agreement-btn decline' type='link' onClick={() => setShowAgreement(false)}>Decline</Button>
                    <Button className='service-agreement-btn accept' type='primary' disabled={!checked || loading} loading={loading} onClick={() => onAcceptAgreement()}>Accept  </Button>
                </div>
            </div>
        )
    }
    const displayAgreement = () => {
        return (
            <Modal
                className='sign-up-service-agreement-modal'
                styles={{ mask: { backgroundColor: 'rgba(0, 0, 0, 0.75)' } }}
                // destroyOnClose
                maskClosable={true}
                width={610}
                open={showAgreement}
                centered={true}
                title=""
                onCancel={() => {
                    setShowAgreement(false)
                }}
                footer={footer}
            >
                <div className='service-agreement-title'>Software as a service agreement</div>
                <div className='service-agreement-content'>
                    <Terms />
                </div>
            </Modal>
        )
    }

    const onFinish = async () => {
        if (nameCheckTimerRef.current) {
            window.clearTimeout(nameCheckTimerRef.current)
            nameCheckTimerRef.current = null
        }
        if (nameChecking) return
        const available = await checkOrganizationName()
        if (!available) return
        setShowAgreement(true)
    }



    const validateMessages = {
        required: 'Please enter your ${label}.',
        types: {
            email: '${label} is not a valid email!',
        }
    }
    return (
        <>
            <Row className='signup-layout'>
                <Col span={24}>
                    <div className='Welcome'>Welcome to Zeitro!</div>
                    <div className='inter'>Please provide the following information. Fields marked with <span className='requiredMark'>*</span> are required. </div>
                    <Form
                        className=''
                        style={{ marginTop: '30px' }}
                        layout='vertical'
                        form={form}

                        onFinish={onFinish}
                        validateMessages={validateMessages}
                        size="large"
                    >
                        <Row>
                            <Col span={11}>
                                <Form.Item
                                    name="FirstName"
                                    label="First Name"
                                    rules={[
                                        {
                                            required: true,
                                        },
                                    ]}
                                >
                                    <Input
                                        placeholder="Enter legal first name"
                                        onFocus={(e) => handleFocus(e)}
                                        onBlur={(e) => handleBlur(e)}
                                    />
                                </Form.Item>
                            </Col>
                            <Col span={11} push={2}>
                                <Form.Item
                                    name="LastName"
                                    label="Last Name"
                                    rules={[
                                        {
                                            required: true,
                                        },
                                    ]}
                                >
                                    <Input
                                        placeholder="Enter legal last name"
                                        onFocus={(e) => handleFocus(e)}
                                        onBlur={(e) => handleBlur(e)}
                                    />
                                </Form.Item>
                            </Col>
                        </Row>

                        <Row>
                            <Col span={24}>
                                <Form.Item
                                    name="Password"
                                    label="Please create your password"
                                    rules={[
                                        {
                                            required: true,
                                            message: 'Please input your password!',
                                        },
                                        {
                                            pattern: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)[A-Za-z\d!@#$%^&*.,]{8,}$/,
                                            message:
                                                'Password must be at least 8 characters, contain at least one uppercase letter, one lowercase letter, and one number.',
                                        },
                                    ]}
                                >
                                    <Input
                                        type='password'
                                        placeholder="Password must contain at least 8 characters, 1 lowercase, 1 upper case and 1 number"
                                        onFocus={(e) => handleFocus(e)}
                                        onBlur={(e) => handleBlur(e)}
                                        autoComplete="new-password"
                                        autoCorrect="off"
                                        autoCapitalize="off"
                                        spellCheck="false"
                                    />
                                </Form.Item>
                            </Col>
                        </Row>
                        <Row>
                            <Col span={24}>
                                <Form.Item
                                    name="IndividualNMLS"
                                    label="NMLS ID"
                                    rules={[
                                        {
                                            required: true,
                                        },
                                    ]}
                                >
                                    <Input
                                        placeholder="Enter your individual NMLS ID"
                                        onFocus={(e) => handleFocus(e)}
                                        onBlur={(e) => handleBlur(e)}
                                    />
                                </Form.Item>
                            </Col>
                        </Row>
                        <Row>
                            <Col span={11} >
                                <Form.Item
                                    name="CompanyName"
                                    validateStatus={nameChecking ? 'validating' : nameDuplicate ? 'error' : undefined}
                                    help={nameDuplicate ? ORGANIZATION_NAME_TAKEN_MESSAGE : undefined}
                                    label={(<div className='d-flex'>
                                        <Tooltip title={nameChecking ? 'Checking organization name...' : undefined}>
                                            <div style={{ display: 'inline-flex', alignItems: 'center' }}>
                                                Organization Name
                                                {nameChecking && <LoadingOutlined spin style={{ marginLeft: 8, fontSize: 12 }} />}
                                            </div>
                                        </Tooltip>
                                        <div className='Popover'>
                                            <Popover placement="right" content={(
                                                <div >
                                                    If you're an individual loan officer, you could use your own name as the organization name.
                                                </div>
                                            )}>
                                                <QuestionCircleOutlined style={{ marginLeft: 8, marginTop: 5 }} />
                                            </Popover>
                                        </div>

                                    </div>)}
                                    rules={[
                                        { required: true },
                                        {
                                            validator: async (_, value) => {
                                                if (!value?.trim() || nameChecking) return Promise.resolve()
                                                if (nameDuplicate) {
                                                    return Promise.reject(new Error(ORGANIZATION_NAME_TAKEN_MESSAGE))
                                                }
                                                return Promise.resolve()
                                            },
                                        },
                                    ]}
                                >
                                    <Input
                                        placeholder="Enter the company name"
                                        onFocus={(e) => handleFocus(e)}
                                        onBlur={handleCompanyNameBlur}
                                    />
                                </Form.Item>
                            </Col>
                            <Col span={11} push={2}>
                                <Form.Item
                                    name="BrokerageNMLS"
                                    label="Brokerage NMLS ID"

                                >
                                    <Input
                                        placeholder="Enter your company's NMLS ID"
                                        onFocus={(e) => handleFocus(e)}
                                        onBlur={(e) => handleBlur(e)}
                                    />
                                </Form.Item>
                            </Col>
                        </Row>


                        <div style={{ display: 'flex', justifyContent: "center", marginTop: "30px" }}>
                            <Button style={{ width: '150px', marginRight: '20px', background: "#fff" }} className='ant-btn-default-zeitro' onClick={() => { back() }}>Back</Button>
                            <Button disabled={!goodtogo || nameChecking || nameDuplicate} loading={loading} htmlType="submit" type='primary' style={{ width: '150px' }}>Create account</Button>

                        </div>
                    </Form>
                </Col>
            </Row >
            {displayAgreement()}
        </>
    )

}

function SignUpPage(props, ref) {
    const location = useLocation()
    const utmSource = new URLSearchParams(location.search).get('utm_source')
    const utmParamsObject = new URLSearchParams(location.search)
    const utmParams = {}
    for (const [key, value] of utmParamsObject.entries()) {
        utmParams[key] = value
    }

    const [pageTimes, setPageTimes] = useState({
        inputEmailAddressPage: {
            componentTime: {
                startTime: null,
                totalDuration: 0.0
            },
            inputField: []
        },
    });

    const previousPageTimes = useRef(null);
    const [currentFocusTime, setCurrentFocusTime] = useState(null);

    useEffect(() => {
        track(STATSIG.COMMON.VIEW, PAGE.SIGNUP, { page: PAGE.SIGNUP })
    }, [])

    const [form] = Form.useForm()
    const formInitial = {

    }
    const [showPersonalInformation, setShowPersonalInformation] = useState('')
    const [signData, setSignData] = useImmer({})
    const validateMessages = {
        required: 'Please enter ${label}.',
        types: {
            email: '${label} is not a valid email!',
        }
    }
    const onFinish = (val) => {
        setSignData(val)
        setShowPersonalInformation('email')

    }

    // 这个方法是快捷登陆直接调用的，所以不需要有验证码流程
    function onRegisterEmail(val){
        setSignData(val)
        setShowPersonalInformation(2)
    }

    useEffect(() => {
        setPageTimes(prev => ({
            ...prev,
            inputEmailAddressPage: {
                ...prev.inputEmailAddressPage,
                componentTime: {
                    ...prev.inputEmailAddressPage.componentTime,
                    startTime: new Date()
                }
            }
        }));
        previousPageTimes.current = new Date();
    }, [])

    useEffect(() => {
        //Update the dwell time of the input email address component
        if (showPersonalInformation === "email") {
            const currentTime = new Date();
            const duration = parseFloat(((currentTime - previousPageTimes.current) / 1000.0).toFixed(1));
            setPageTimes(prev => ({
                ...prev,
                inputEmailAddressPage: {
                    ...prev.inputEmailAddressPage,
                    componentTime: {
                        ...prev.inputEmailAddressPage.componentTime,
                        totalDuration: parseFloat((prev.inputEmailAddressPage.componentTime.totalDuration + duration).toFixed(1))
                    }
                }
            }));
        }
        previousPageTimes.current = new Date();
    }, [showPersonalInformation]);

    const handleFocus = (e) => {
        const fieldName = e.target.id;
        const fieldValue = e.target.value;

        setCurrentFocusTime(new Date());

        setPageTimes(prev => {
            const existingField = prev.inputEmailAddressPage.inputField.find(
                field => field.name === fieldName
            );

            if (!existingField) {
                return {
                    ...prev,
                    inputEmailAddressPage: {
                        ...prev.inputEmailAddressPage,
                        inputField: [
                            ...prev.inputEmailAddressPage.inputField,
                            {
                                name: fieldName,
                                value: fieldValue,
                                focusTime: 0,
                                startTime: new Date()
                            }
                        ]
                    }
                };
            }
            return prev;
        });
    };

    const handleBlur = (e) => {
        const fieldName = e.target.id;
        const fieldValue = e.target.value;

        if (currentFocusTime) {
            const endTime = new Date();
            const timeSpent = parseFloat(((endTime - currentFocusTime) / 1000.0).toFixed(1));

            setPageTimes(prev => ({
                ...prev,
                inputEmailAddressPage: {
                    ...prev.inputEmailAddressPage,
                    inputField: prev.inputEmailAddressPage.inputField.map(field => {
                        if (field.name === fieldName) {
                            const currentFocusTime = typeof field.focusTime === 'string'
                                ? parseFloat(field.focusTime)
                                : (field.focusTime || 0);

                            return {
                                ...field,
                                value: fieldValue,
                                focusTime: parseFloat((currentFocusTime + timeSpent).toFixed(1))
                            };
                        }
                        return field;
                    })
                }
            }));
        }

        setCurrentFocusTime(null);
    };

    const displayCarousel = () => {
        const contentStyle = {
            width: '100%',
            height: '100vh',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center'
          };
        return (
            <div className='los-carousel-box'>
                <div className='content-box'>
                <Carousel
                    className='create-carousel'
                    // autoplay
                >
                    <div>
                        <div style={contentStyle}>
                            <div className='center-box'>
                                <div>Strata AI</div>
                                <div style={{fontSize: 16, color:"#222222", fontWeight: 400, marginBottom: 20}}>AI-powered expertise across Conventional, Non-QM, FHA, VA, USDA, and Business-Purpose Loan guidelines allows you to solve complex scenarios quickly and efficiently. </div>
                                <img src='/images/carousel-img-1.svg' />
                            </div>
                        </div>
                    </div>
                    <div>
                        <div style={contentStyle}>
                            <div className='center-box'>
                                <div>AI-powered efficiency</div>
                                <div style={{fontSize: 16, color:"#222222", fontWeight: 400, marginBottom: 20}}>Streamline every aspect of mortgage origination, from rate quotes to document processing, saving hours on every loan.</div>
                                <img src='/images/carousel-img-2.svg' />
                            </div>
                        </div>
                    </div>
                    {/* <div>
                        <div style={contentStyle}>
                            <div style={{padding: '0 44px', width: 'auto'}} className='center-box'>
                                <div style={{padding: '0 26px'}}>Wholesale integration</div>
                                <div style={{fontSize: 16, color:"#222222", fontWeight: 400, marginBottom: 20, padding: '0 26px'}}>Automatically connect to multiple wholesalers, ensuring you always have the most competitive rates for your borrowers.</div>
                                <div >
                                    <img src='/images/carousel-img-3.svg' />
                                </div>
                            </div>
                        </div>
                    </div> */}
                </Carousel>
                </div>
            </div>
        )
    }



    const displayImage = () => {
        return (
            <div className='los-img-box'>
                <div className='los-logo-box'>
                    <img src='/images/login-logo.png' />
                </div>
                <div className='los-bg-box'>
                    <img src='/images/pos.png' />
                </div>
                <div className='los-img-box-content'>
                    <div className='los-img-box-content-title'>
                        We make the best loan professionals superhuman 
                    </div>
                    {/* <div className='los-img-box-content-name'>
                    Bob A, Broker owner
                    </div> */}
                    <div className='los-img-box-content-address'>
                        Zeitro is the AI powered origination platform that helps mortgage brokers close 30% more loans.
                    </div>
                </div>
            </div>
        )
    }

    function onToSignIn(){
        if(props.onToSignIn){
            props.onToSignIn()
        }else{
            const search = window.location.search || ''
            const basePath = '/'
            window.location.href = search ? `${basePath}${search}` : basePath
        }
    }

    useImperativeHandle(ref, () => ({
        onFinish,
        onRegisterEmail
    }));

    return (
        <div className='los-signup-wrap'>
            {props.banner ? 
            <img src={props.banner} className="iframe-login-modal-left-img-panel" /> 
            :  <div className='los-img-box-container'>{displayImage()}</div>
            }
            <div className="sign-up-box">
                <img className='mobile-logo-svg' src='/images/logo.svg' style={{width: '90px', top: '15px', left: '20px', position: 'absolute'}} />
                    {!(props.onToSignIn &&signData.emailaddress) ?  <>
                    <div className="signin-link-top">
                        Already have an account? <span className='to-signIn' onClick={onToSignIn}>Sign in</span>
                    </div>
                    <div className='mobile-nav'>
                        <div className='mobile-logo'><img src='/images/mobile-logo.svg' className='w-100' /></div>
                        <div className="signin-link-mobile">
                            Already have an account? <span className='to-signIn' onClick={onToSignIn}>Sign in</span>
                        </div>
                    </div>
                </> : null }

                <div className="sign-up-content">
                    <div className='d-flex flex-column w-100 text-center'>
                        <div className='create-los-account'>
                        {showPersonalInformation === '' ?
                            <div className='create-centerBox'>
                                <div className='create-title'>Create your account</div>
                                <Form

                                    layout='vertical'
                                    style={{ marginTop: '30px' }}
                                    form={form}
                                    initialValues={formInitial}
                                    onFinish={onFinish}
                                    validateMessages={validateMessages}
                                    size="large"
                                    requiredMark={false}
                                >

                                    <Row>
                                        <Col span={24}>
                                            <Form.Item
                                                name="emailaddress"
                                                label="Email Address"
                                                rules={[
                                                    {
                                                        required: true,
                                                        type: 'email',
                                                        validateTrigger: ['onSubmit']
                                                    },
                                                ]}
                                            >
                                                <Input
                                                    style={{height: '50px'}}
                                                    placeholder="Enter your work email"
                                                    onFocus={(e) => handleFocus(e)}
                                                    onBlur={(e) => handleBlur(e)}
                                                />
                                            </Form.Item>
                                        </Col>
                                    </Row>

                                    <Button htmlType="submit" className='create-btn sign-zeitro' block style={{ marginTop: '0px', height: '50px' }}>Continue</Button>
                                </Form>
                                {/* <Divider plain>or sign up with</Divider> */}


                            </div> : showPersonalInformation === 'email' ? <NewEmailVerification fromSignUp email={signData.emailaddress} onSuccess={props.onSuccess} utmSource={utmSource} back={(e) => {
                                setShowPersonalInformation(e)
                                }} /> : <PersonalInformation pageTime={pageTimes} signData={signData} utmSource={utmSource} utmParams={utmParams} onSuccess={props.onSuccess} back={() => {
                                setShowPersonalInformation('')
                            }} />
                        }
                        </div>
                        <div className='create-copyright'>© {new Date().getFullYear()} Zeitro. All rights reserved.</div>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default React.forwardRef(SignUpPage);
